import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReadOnlyBanner } from "@/components/Layout";
import WorkflowStepper from "@/components/WorkflowStepper";
import MarkdownView from "@/components/MarkdownView";
import CustomTemplatePanel from "@/components/CustomTemplatePanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectSeparator } from "@/components/ui/select";
import { ReportStyleOptions, CUSTOM_STYLE, styleTriggerLabel } from "@/components/ReportStyleOptions";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, FileDown, Share2, Save, Wand2, Eye, Pencil, Copy, Mail, MessageCircle, FileText, ArrowLeft, X, Bookmark, Lock, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "@/i18n";

const RANGES = ["daily", "weekly", "monthly", "quarterly", "yearly", "custom"];
const LENGTHS = ["~1 page", "2-5 pages", "5-10 pages", "10-20 pages", "over 20 pages"];
const LENGTH_KEYS = { "~1 page": "p1", "2-5 pages": "p2_5", "5-10 pages": "p5_10", "10-20 pages": "p10_20", "over 20 pages": "p20plus" };

function rangeDates(type) {
  const now = new Date();
  const iso = (d) => d.toISOString().slice(0, 10);
  const s = new Date(now), e = new Date(now);
  if (type === "daily") { /* today */ }
  else if (type === "weekly") { s.setDate(now.getDate() - 6); }
  else if (type === "monthly") { s.setDate(1); }
  else if (type === "quarterly") { const q = Math.floor(now.getMonth() / 3); s.setMonth(q * 3, 1); }
  else if (type === "yearly") { s.setMonth(0, 1); }
  return { start: iso(s), end: iso(e) };
}

export default function Reports() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const readOnly = user?.subscription_status === "read_only";
  const [view, setView] = useState("list"); // list | config | editor
  const [saved, setSaved] = useState([]);
  const [shareFilter, setShareFilter] = useState("all"); // all | shared | private
  const [cfg, setCfg] = useState({ range_type: "weekly", style: "Standard", length: "~1 page", language: (user?.language || "en"), ...rangeDates("weekly") });
  const [customTpl, setCustomTpl] = useState(null);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [revising, setRevising] = useState(false);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [preview, setPreview] = useState(true);
  const [shareDialog, setShareDialog] = useState(null);
  const [usage, setUsage] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [includeScope, setIncludeScope] = useState(true);
  const [hasScope, setHasScope] = useState(false);

  const loadSaved = async () => {
    try { const res = await api.get("/reports", { params: { status: "saved" } }); setSaved(res.data); }
    catch (err) { toast.error(apiError(err)); }
  };
  const loadUsage = async () => {
    try { const res = await api.get("/billing/usage"); setUsage(res.data); } catch { /* ignore */ }
  };
  const loadTemplates = async () => {
    try { const res = await api.get("/templates"); setTemplates(res.data); } catch { /* ignore */ }
  };
  useEffect(() => { loadSaved(); loadUsage(); loadTemplates();
    api.get("/context").then((r) => setHasScope(!!r.data?.scope?.content)).catch(() => {});
  }, []);

  const applyTemplate = (tpl) => { setCfg((c) => ({ ...c, style: tpl.style, length: tpl.length })); toast.success(t("reports.applied", { name: tpl.name })); };
  const saveTemplate = async () => {
    const name = window.prompt(t("reports.nameTemplatePrompt"), `${cfg.style} · ${cfg.length}`);
    if (!name) return;
    try { await api.post("/templates", { name, style: cfg.style, length: cfg.length }); toast.success(t("reports.templateSaved")); loadTemplates(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/templates/${id}`); loadTemplates(); } catch (err) { toast.error(apiError(err)); }
  };

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const id = location.state?.openReportId;
    if (id) openSaved(id);
    // eslint-disable-next-line
  }, [location.state]);

  const changeRange = (type) => setCfg({ ...cfg, range_type: type, ...(type !== "custom" ? rangeDates(type) : {}) });

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/reports/generate", {
        range_type: cfg.range_type, style: cfg.style, length: cfg.length,
        start_date: cfg.start, end_date: cfg.end, include_scope: includeScope,
        language: cfg.language,
        ...(cfg.style === CUSTOM_STYLE && customTpl ? customTpl : {}),
      });
      setReport(res.data); setComment(""); setView("editor"); setPreview(true); loadUsage();
    } catch (err) { toast.error(apiError(err)); }
    finally { setGenerating(false); }
  };

  const revise = async () => {
    if (!comment.trim()) { toast.error(t("reports.describeChange")); return; }
    setRevising(true);
    try {
      const res = await api.post(`/reports/${report.id}/revise`, { comment });
      setReport(res.data); setComment(""); toast.success(t("reports.reportRevised"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setRevising(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/reports/${report.id}`, { title: report.title, content: report.content, shared_with_supervisor: !!report.shared_with_supervisor });
      setReport(res.data); toast.success(t("reports.reportSaved")); loadSaved();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const updateSharing = async (id, val) => {
    try {
      await api.put(`/reports/${id}/sharing`, { shared_with_supervisor: val });
      setSaved((s) => s.map((r) => r.id === id ? { ...r, shared_with_supervisor: val } : r));
      toast.success(val ? t("reports.sharedToast") : t("reports.privateToast"));
    } catch (err) { toast.error(apiError(err)); }
  };

  const openSaved = async (id) => {
    try { const res = await api.get(`/reports/${id}`); setReport(res.data); setComment(""); setView("editor"); setPreview(true); }
    catch (err) { toast.error(apiError(err)); }
  };

  const download = async (format) => {
    try {
      const res = await api.get(`/reports/${report.id}/export`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${report.title}.${format}`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(t("reports.exportFailed")); }
  };

  const share = async () => {
    try {
      const res = await api.post(`/reports/${report.id}/share`);
      setShareDialog(res.data);
    } catch (err) { toast.error(apiError(err)); }
  };

  const stepFor = () => view === "config" ? 2 : view === "editor" ? 3 : 2;

  return (
    <div className="animate-fade-up">
      <ReadOnlyBanner />
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-navy">{t("reports.title")}</h1>
          <p className="text-slatebody mt-1">{t("reports.subtitle")}</p>
          <p className="text-xs text-slatebody mt-2" data-testid="reports-trust-msg">{t("reports.trustMsg")}</p>
        </div>
        {view !== "list" && (
          <Button variant="outline" onClick={() => { setView("list"); setReport(null); loadSaved(); }} className="gap-1.5" data-testid="reports-back">
            <ArrowLeft className="h-4 w-4" /> {t("reports.allReports")}
          </Button>
        )}
      </div>

      <WorkflowStepper current={stepFor()} />

      {view === "list" && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 mb-6">
            <div className="flex items-center gap-3 mb-1">
              <div className="h-9 w-9 rounded-md bg-accent flex items-center justify-center"><Sparkles className="h-4 w-4 text-[#202123]" /></div>
              <h2 className="font-display text-lg font-semibold text-navy">{t("reports.generateNew")}</h2>
            </div>
            <p className="text-sm text-slatebody mb-6">{t("reports.generateNewDesc")}</p>
            {usage && !usage.unlimited && (
              <div className="mb-5 flex items-center gap-2" data-testid="reports-usage">
                <span className="font-mono text-xs rounded-full bg-accent text-accent-foreground px-2.5 py-1">{t("reports.reportsLeftToday", { left: usage.day_left, limit: usage.day_limit })}</span>
                {usage.day_left === 0 && (
                  <button onClick={() => navigate("/app/billing")} className="text-xs text-[#202123] font-medium hover:underline" data-testid="usage-upgrade">{t("reports.upgradeForMore")}</button>
                )}
              </div>
            )}
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" disabled={readOnly || (usage && !usage.unlimited && usage.day_left === 0)} onClick={() => setView("config")} data-testid="new-report-btn">
              <Sparkles className="h-4 w-4" /> {t("reports.newReport")}
            </Button>
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <h3 className="font-display font-semibold text-navy">{t("reports.savedReports")}</h3>
            {user?.account_type === "organization" && saved.some((r) => !r.is_compiled) && (
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="reports-share-filter">
                {["all", "shared", "private"].map((f) => (
                  <button key={f} onClick={() => setShareFilter(f)} data-testid={`share-filter-${f}`}
                    className={`px-3 py-1 text-xs rounded-md transition-colors ${shareFilter === f ? "bg-[#ECECEC] text-[#202123] font-medium" : "text-slatebody hover:text-navy"}`}>
                    {t(`reports.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-3" data-testid="saved-reports">
            {(() => {
              const visible = saved.filter((r) =>
                shareFilter === "all" || r.is_compiled ? true
                  : shareFilter === "shared" ? r.shared_with_supervisor : !r.shared_with_supervisor);
              if (saved.length === 0) return (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center text-slatebody">
                  {t("reports.noSavedReports")}
                </div>
              );
              if (visible.length === 0) return (
                <div className="rounded-xl border border-dashed border-slate-300 bg-white p-8 text-center text-slatebody" data-testid="no-filtered-reports">
                  {t("reports.noFilteredReports")}
                </div>
              );
              return visible.map((r) => (
              <div key={r.id} onClick={() => openSaved(r.id)} data-testid={`saved-${r.id}`} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter") openSaved(r.id); }}
                className="w-full text-left rounded-xl border border-slate-200 bg-white p-5 flex items-center gap-4 hover:border-[#C5C5D0] transition-colors cursor-pointer">
                <div className="h-10 w-10 rounded-md bg-accent flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-[#202123]" /></div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-navy truncate">{r.title}</p>
                  <p className="font-mono text-[11px] text-slatebody mt-0.5">{r.style} · {r.length} · {t("reports.updated")} {new Date(r.updated_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                  {user?.account_type === "organization" && !r.is_compiled && (
                    <button
                      onClick={(e) => { e.stopPropagation(); updateSharing(r.id, !r.shared_with_supervisor); }}
                      data-testid={`report-share-toggle-${r.id}`}
                      title={r.shared_with_supervisor ? t("reports.clickToUnshare") : t("reports.clickToShare")}
                      className={`inline-flex items-center gap-1 text-[11px] rounded-full border px-2 py-0.5 transition-colors ${r.shared_with_supervisor ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" : "bg-slate-100 text-slatebody border-slate-200 hover:bg-slate-200"}`}>
                      {r.shared_with_supervisor
                        ? <><Eye className="h-3 w-3" /> {t("reports.badgeShared")}</>
                        : <><Lock className="h-3 w-3" /> {t("reports.badgePrivate")}</>}
                    </button>
                  )}
                  {r.is_compiled && <span className="text-[11px] rounded-full bg-accent px-2 py-0.5 text-accent-foreground">{t("reports.badgeTeam")}</span>}
                </div>
              </div>
              ));
            })()}
          </div>
        </>
      )}

      {view === "config" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 md:p-8 max-w-2xl" data-testid="report-config">
          <h2 className="font-display text-lg font-semibold text-navy mb-4">{t("reports.configureReport")}</h2>
          {templates.length > 0 && (
            <div className="mb-6" data-testid="template-chips">
              <p className="font-mono text-[11px] uppercase text-slatebody mb-2">{t("reports.yourTemplates")}</p>
              <div className="flex flex-wrap gap-2">
                {templates.map((tpl) => (
                  <button key={tpl.id} onClick={() => applyTemplate(tpl)} data-testid={`template-${tpl.id}`}
                    className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-sm text-navy hover:border-[#C5C5D0] hover:bg-accent transition-colors">
                    <span>{tpl.name}</span>
                    <X className="h-3.5 w-3.5 text-slatebody hover:text-red-500" onClick={(e) => deleteTemplate(tpl.id, e)} data-testid={`template-del-${tpl.id}`} />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-5">
            <div>
              <Label>{t("reports.range")}</Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {RANGES.map((r) => (
                  <button key={r} onClick={() => changeRange(r)} data-testid={`range-${r}`}
                    className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${cfg.range_type === r ? "border-[#C5C5D0] bg-accent text-accent-foreground" : "border-slate-300 text-slatebody hover:border-slate-400"}`}>
                    {t(`reports.ranges.${r}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t("reports.from")}</Label><Input type="date" value={cfg.start} onChange={(e) => setCfg({ ...cfg, start: e.target.value })} className="mt-1.5 font-mono" data-testid="report-start" /></div>
              <div><Label>{t("reports.to")}</Label><Input type="date" value={cfg.end} onChange={(e) => setCfg({ ...cfg, end: e.target.value })} className="mt-1.5 font-mono" data-testid="report-end" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("reports.style")}</Label>
                <Select value={cfg.style} onValueChange={(v) => setCfg({ ...cfg, style: v })}>
                  <SelectTrigger className="mt-1.5 h-auto min-h-10" data-testid="report-style"><span className="text-left">{styleTriggerLabel(t, cfg.style)}</span></SelectTrigger>
                  <SelectContent>
                    <ReportStyleOptions t={t} customTestId="report-style-custom" />
                  </SelectContent>
                </Select>
                {cfg.style === CUSTOM_STYLE && <CustomTemplatePanel api={api} onChange={setCustomTpl} />}
              </div>
              <div>
                <Label>{t("reports.length")}</Label>
                <Select value={cfg.length} onValueChange={(v) => setCfg({ ...cfg, length: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="report-length"><SelectValue /></SelectTrigger>
                  <SelectContent>{LENGTHS.map((l) => <SelectItem key={l} value={l}>{t(`reports.lengths.${LENGTH_KEYS[l]}`)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="flex items-center gap-1.5"><Globe className="h-3.5 w-3.5 text-[#202123]" /> {t("reports.reportLanguage")}</Label>
              <Select value={cfg.language} onValueChange={(v) => setCfg({ ...cfg, language: v })}>
                <SelectTrigger className="mt-1.5" data-testid="report-language"><SelectValue /></SelectTrigger>
                <SelectContent>{SUPPORTED_LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code}>{l.native}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-slatebody mt-1">{t("reports.reportLanguageDesc")}</p>
            </div>
            {hasScope && (
              <label className="flex items-start gap-2.5 rounded-md border border-slate-200 bg-slate-50 px-3.5 py-3 cursor-pointer" data-testid="include-scope-row">
                <input type="checkbox" checked={includeScope} onChange={(e) => setIncludeScope(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-[#202123]" data-testid="include-scope-checkbox" />
                <span className="text-sm text-navy">{t("reports.useScope")}
                  <span className="block text-xs text-slatebody mt-0.5">{t("reports.useScopeDesc")}</span>
                </span>
              </label>
            )}
            <Button className="bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5 w-full" disabled={generating} onClick={generate} data-testid="generate-btn">
              <Sparkles className="h-4 w-4" /> {generating ? t("common.generating") : t("reports.generate")}
            </Button>
            <button onClick={saveTemplate} className="w-full text-sm text-[#202123] font-medium hover:underline inline-flex items-center justify-center gap-1.5" data-testid="save-template-btn">
              <Bookmark className="h-3.5 w-3.5" /> {t("reports.saveAsTemplate")}
            </button>
            {generating && <p className="text-xs text-slatebody text-center">{t("reports.generatingHint")}</p>}
          </div>
        </div>
      )}

      {view === "editor" && report && (
        <div className="grid lg:grid-cols-3 gap-6" data-testid="report-editor">
          <div className="lg:col-span-2 space-y-4">
            <Input value={report.title} onChange={(e) => setReport({ ...report, title: e.target.value })}
              className="font-display text-lg font-semibold" data-testid="report-title" />
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
                <span className="font-mono text-[11px] uppercase text-slatebody">{report.status === "saved" ? t("reports.statusSaved") : t("reports.statusDraft")}</span>
                <div className="flex gap-1">
                  <button onClick={() => setPreview(true)} className={`p-1.5 rounded ${preview ? "bg-accent text-[#202123]" : "text-slatebody"}`} data-testid="preview-toggle"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => setPreview(false)} className={`p-1.5 rounded ${!preview ? "bg-accent text-[#202123]" : "text-slatebody"}`} data-testid="edit-toggle"><Pencil className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="p-6">
                {preview
                  ? <MarkdownView content={report.content} />
                  : <Textarea value={report.content} onChange={(e) => setReport({ ...report, content: e.target.value })} rows={24} className="font-mono text-sm" data-testid="report-content-edit" />}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 bg-white p-5">
              <h3 className="font-display font-semibold text-navy mb-3 flex items-center gap-1.5"><Wand2 className="h-4 w-4 text-[#202123]" /> {t("reports.askAiRevise")}</h3>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} disabled={readOnly}
                placeholder={t("reports.reviseInlinePlaceholder")} className="resize-none" data-testid="revise-comment" />
              <Button className="w-full mt-3 bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" disabled={revising || readOnly} onClick={revise} data-testid="revise-btn">
                {revising ? t("reports.revising") : t("reports.reviseReport")}
              </Button>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-2.5">
              {user?.account_type === "organization" && !report.is_compiled && (
                <label className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 bg-background p-3" data-testid="editor-share-row">
                  <span className="text-sm text-navy min-w-0">{t("reports.shareWithTeam")}
                    <span className="block text-xs text-slatebody">{t("reports.shareWithTeamHint")}</span>
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-xs font-medium ${report.shared_with_supervisor ? "text-emerald-600" : "text-slatebody"}`} data-testid="editor-share-state">
                      {report.shared_with_supervisor ? t("reports.stateShared") : t("reports.statePrivate")}
                    </span>
                    <Switch checked={!!report.shared_with_supervisor} onCheckedChange={(v) => setReport({ ...report, shared_with_supervisor: v })} data-testid="editor-share-toggle" />
                  </div>
                </label>
              )}
              <Button className="w-full bg-[#ECECEC] hover:bg-[#E0E0E0] text-[#202123] border border-[#C5C5D0] gap-1.5" disabled={saving} onClick={save} data-testid="save-report-btn">
                <Save className="h-4 w-4" /> {saving ? t("reports.saving") : t("reports.saveFinalize")}
              </Button>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" className="gap-1.5" onClick={() => download("pdf")} data-testid="export-pdf"><FileDown className="h-4 w-4" /> {t("reports.pdf")}</Button>
                <Button variant="outline" className="gap-1.5" onClick={() => download("docx")} data-testid="export-docx"><FileDown className="h-4 w-4" /> {t("reports.word")}</Button>
              </div>
              <Button variant="outline" className="w-full gap-1.5" onClick={share} data-testid="share-btn"><Share2 className="h-4 w-4" /> {t("reports.share")}</Button>
              <p className="text-[11px] text-slatebody text-center pt-1">{t("reports.saveBeforeExport")}</p>
            </div>
          </div>
        </div>
      )}

      <ShareDialog data={shareDialog} onClose={() => setShareDialog(null)} title={report?.title} />
    </div>
  );
}

function ShareDialog({ data, onClose, title }) {
  const { t } = useTranslation();
  if (!data) return null;
  const link = data.url;
  const mailto = `mailto:?subject=${encodeURIComponent(title || "JotBay Report")}&body=${encodeURIComponent("Here is my report: " + link)}`;
  const wa = `https://wa.me/?text=${encodeURIComponent((title || "JotBay Report") + ": " + link)}`;
  const copy = () => { navigator.clipboard.writeText(link); toast.success(t("reports.linkCopied")); };
  return (
    <Dialog open={!!data} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="share-dialog">
        <DialogHeader><DialogTitle className="font-display">{t("reports.shareReport")}</DialogTitle>
          <DialogDescription>{t("reports.shareDialogDesc")}</DialogDescription></DialogHeader>
        <p className="text-sm text-slatebody">{t("reports.shareDialogNote")}</p>
        <div className="flex gap-2">
          <Input value={link} readOnly className="font-mono text-xs" data-testid="share-link" />
          <Button variant="outline" onClick={copy} className="gap-1.5 shrink-0"><Copy className="h-4 w-4" /> {t("reports.copy")}</Button>
        </div>
        <div className="grid grid-cols-3 gap-2 pt-2">
          <a href={mailto} data-testid="share-email"><Button variant="outline" className="w-full gap-1.5"><Mail className="h-4 w-4" /> {t("reports.email")}</Button></a>
          <a href={wa} target="_blank" rel="noreferrer" data-testid="share-whatsapp"><Button variant="outline" className="w-full gap-1.5"><MessageCircle className="h-4 w-4" /> {t("reports.whatsapp")}</Button></a>
          <a href={link} target="_blank" rel="noreferrer" data-testid="share-open"><Button variant="outline" className="w-full gap-1.5"><Eye className="h-4 w-4" /> {t("reports.open")}</Button></a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
